/**
 * Utilidades cliente para el hub de redes (kit manual + UI).
 * La fuente de verdad del copy largo es `admin-actions` → `social_build_kit`.
 */

import { CANONICAL_SITE_ORIGIN, getPublicSiteBase } from '../constants/siteConfig.js';

export { getPublicSiteBase, CANONICAL_SITE_ORIGIN };

export const SOCIAL_NETWORKS = [
  {
    id: 'telegram',
    label: 'Telegram',
    maxChars: 1024,
    automationNote: 'Bandeja @DreamVinylComunidad (foto + texto vía n8n)',
  },
  { id: 'instagram', label: 'Instagram', maxChars: 2200, automationNote: 'Meta Graph API vía n8n' },
  { id: 'facebook', label: 'Facebook', maxChars: 5000, automationNote: 'Meta Graph API vía n8n' },
  { id: 'tiktok', label: 'TikTok', maxChars: 2200, automationNote: 'TikTok Content API vía n8n' },
  { id: 'youtube', label: 'YouTube', maxChars: 5000, automationNote: 'YouTube Data API vía n8n' },
  { id: 'x', label: 'X (Twitter)', maxChars: 275, automationNote: 'API de pago; usar manual si no hay plan' },
  { id: 'linkedin', label: 'LinkedIn', maxChars: 3000, automationNote: 'LinkedIn API vía n8n' },
];

export const SOCIAL_CONTENT_TYPES = {
  noticia: { label: 'Noticia', adminPath: 'noticias' },
  listing: { label: 'Anuncio mercado', adminPath: 'listings' },
  auction: { label: 'Subasta', adminPath: 'subastas' },
  comunidad: { label: 'Comunidad', adminPath: 'comunidad' },
};

/** Ruta relativa a la guía operativa (desde repo). */
export const SOCIAL_GUIDE_DOC_PATH = 'docs/appendix/redes_sociales_hub_guia.md';

export function buildPublicContentUrl(contentType, contentId, siteBase = getPublicSiteBase()) {
  const base = String(siteBase ?? '').replace(/\/+$/, '') || CANONICAL_SITE_ORIGIN;
  const id = encodeURIComponent(String(contentId ?? '').trim());
  if (!id) return base;
  switch (contentType) {
    case 'noticia':
      return `${base}/?noticia=${id}`;
    case 'comunidad':
      return `${base}/?comunidad=${id}`;
    case 'listing':
    case 'auction':
      return `${base}/?listing=${id}`;
    default:
      return base;
  }
}

export function truncateForNetwork(text, maxChars) {
  const s = String(text ?? '').trim();
  if (!maxChars || s.length <= maxChars) return s;
  return s.slice(0, Math.max(0, maxChars - 1)).trimEnd() + '…';
}

/** Arma un .txt descargable a partir del kit devuelto por el servidor. */
export function formatKitAsDownloadText(kit) {
  if (!kit || typeof kit !== 'object') return '';
  const lines = [];
  lines.push('DreamVinyl — kit para redes');
  lines.push(`Título: ${kit.titulo ?? ''}`);
  lines.push(`URL: ${kit.url ?? ''}`);
  if (Array.isArray(kit.hashtags) && kit.hashtags.length) {
    lines.push(`Hashtags: ${kit.hashtags.join(' ')}`);
  }
  lines.push('');
  if (kit.extracto) {
    lines.push('Extracto:');
    lines.push(String(kit.extracto));
    lines.push('');
  }
  const texts = kit.texts && typeof kit.texts === 'object' ? kit.texts : {};
  for (const net of SOCIAL_NETWORKS) {
    const body = texts[net.id];
    if (!body) continue;
    lines.push(`--- ${net.label} ---`);
    lines.push(String(body));
    lines.push('');
  }
  if (Array.isArray(kit.imagen_urls) && kit.imagen_urls.length) {
    lines.push('Imágenes:');
    kit.imagen_urls.forEach(u => lines.push(String(u)));
  }
  return lines.join('\n');
}

export async function copyToClipboard(text) {
  const s = String(text ?? '');
  if (!s) return false;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch (_) {
    /* fallback */
  }
  try {
    if (typeof document === 'undefined') return false;
    const ta = document.createElement('textarea');
    ta.value = s;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_) {
    return false;
  }
}

export function downloadTextFile(filename, text) {
  if (typeof document === 'undefined') return;
  const blob = new Blob([String(text ?? '')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'dreamvinyl.txt';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Descarga imagen remota; si CORS bloquea, abre en nueva pestaña. */
export async function downloadImageFromUrl(url, filename = 'dreamvinyl.jpg') {
  const src = String(url ?? '').trim();
  if (!src) return false;
  try {
    const res = await fetch(src, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch_failed');
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    return true;
  } catch (_) {
    try {
      window.open(src, '_blank', 'noopener,noreferrer');
    } catch {
      /* noop */
    }
    return false;
  }
}
